import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Request, RequestStatus } from "@/types/request";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  REQUEST_PRIORITIES,
  REQUEST_STATUS_OPTIONS,
} from "@/constants/requestConstants";
import { useTranslation } from "react-i18next";
import { useAllServices } from "@/hooks/useHotelServices";
import { useHotel } from "@/hooks/useHotel";
import { formatInTimeZone } from "date-fns-tz";
import { MainGuestCell } from "@/components/pages/settlement/components/table-cells/MainGuestCell";
import { RoomCell } from "@/components/pages/settlement/components/table-cells/RoomCell";
import { Task } from "@/types/tasks";
import { useUpdateTask } from "@/components/pages/requests/hooks/tasks.hooks";
import { MarkOfflinePaymentButton } from "./components/MarkOfflinePaymentButton";
import { REQUEST_SERVICE_PROVIDER_TYPES } from "./utils/requestServiceOptions";

interface EditRequestFormProps {
  request: Request;
  onClose: () => void;
  onSuccess?: (id: string, updatedRequest: Partial<Request>) => void;
}

export const EditRequestForm: React.FC<EditRequestFormProps> = ({
  request,
  onClose,
  onSuccess,
}) => {
  const noServiceSelectValue = "__NO_SERVICE__";
  const { t } = useTranslation();
  const updateTaskMutation = useUpdateTask();
  const hasNoService = !request.service?.id && !request.serviceId;
  const [formData, setFormData] = useState({
    type: request.type,
    status: (request.tasks?.[0] as Task)?.status,
    serviceId: request.service?.id ?? request.serviceId ?? undefined,
    priority: request.priority,
    dueDateTime: request.dueDateTime,
    notes: request.notes || "",
  });
  type EditRequestFormState = typeof formData;
  const [dueTime, setDueTime] = useState("");

  useEffect(() => {
    if (request.dueDateTime) {
      const date = new Date(request.dueDateTime);
      const timeString = formatInTimeZone(date, "UTC", "HH:mm");
      setDueTime(timeString);
    }
  }, [request.dueDateTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.dueDateTime) {
      toast.error(t("requests.form.pleaseFillinAllRequiredFields"));
      return;
    }

    try {
      const { status, ...requestData } = formData;
      const task = request.tasks?.[0] as Task | undefined;

      if (task?.id && status !== task.status) {
        await updateTaskMutation.mutateAsync({
          id: task.id,
          data: { status: status as RequestStatus },
        });
      }

      onSuccess?.(request.id || "", requestData);
      onClose();
    } catch (error) {
      console.error(t("requests.form.failedToUpdateRequest"), error);
    }
  };

  const handleInputChange = <K extends keyof EditRequestFormState>(
    field: K,
    value: EditRequestFormState[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const { hotelId } = useHotel();
  const { data: servicesData } = useAllServices(
    hotelId!,
    REQUEST_SERVICE_PROVIDER_TYPES,
  );

  const services = servicesData?.data ?? [];

  return (
    <div className="max-h-[70vh] overflow-y-auto pr-2">
      {request.guest?.profile && (
        <div className={"mb-2 flex flex-row gap-2"}>
          <MainGuestCell
            id={request.guestId ?? undefined}
            firstName={request.guest?.profile.firstName}
            lastName={`${request.guest?.profile.lastName}${request?.booking?.room ? "," : ""}`}
            nationality={request.guest?.profile.language}
          />
          {request?.booking?.room && (
            <RoomCell
              id={request.booking?.room.id}
              roomNumber={request.booking?.room?.number}
              roomType={request.booking?.room?.type}
              className={"hover:cursor-pointer hover:underline"}
            />
          )}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Select
            value={formData.status}
            onValueChange={(value) =>
              handleInputChange("status", value as RequestStatus)
            }
          >
            <SelectTrigger className="w-full justify-between">
              <SelectValue placeholder={t("requests.form.selectStatus")} />
            </SelectTrigger>
            <SelectContent>
              {REQUEST_STATUS_OPTIONS.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <MarkOfflinePaymentButton request={request} onSuccess={onClose} />

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Input
              id="dueDateTime"
              type="date"
              value={formData.dueDateTime?.split("T")[0] || ""}
              onChange={(e) => {
                const date = e.target.value;
                const time = dueTime || "12:00";
                handleInputChange("dueDateTime", `${date}T${time}:00.000Z`);
              }}
              required
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Input
              id="dueTime"
              type="time"
              value={dueTime}
              onChange={(e) => {
                setDueTime(e.target.value);
                const date =
                  formData.dueDateTime?.split("T")[0] ||
                  format(new Date(), "yyyy-MM-dd");
                handleInputChange(
                  "dueDateTime",
                  `${date}T${e.target.value}:00.000Z`,
                );
              }}
              required
              className="w-full"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Select
            value={formData.priority ? "Star" : "normal"}
            onValueChange={(value) =>
              handleInputChange("priority", value === "Star")
            }
          >
            <SelectTrigger className="w-full justify-between">
              <SelectValue placeholder={t("requests.form.selectPriority")} />
            </SelectTrigger>
            <SelectContent>
              {REQUEST_PRIORITIES.map((priority) => (
                <SelectItem
                  key={priority.label}
                  value={priority.value ? "Star" : "normal"}
                >
                  {priority.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Select
            value={
              formData.serviceId ||
              (hasNoService ? noServiceSelectValue : undefined)
            }
            onValueChange={(value) =>
              handleInputChange(
                "serviceId",
                value === noServiceSelectValue ? undefined : value,
              )
            }
          >
            <SelectTrigger className="w-full justify-between">
              <SelectValue placeholder={t("requests.form.selectService")} />
            </SelectTrigger>
            <SelectContent>
              {hasNoService ? (
                <SelectItem value={noServiceSelectValue}>
                  {t("requests.form.noService")}
                </SelectItem>
              ) : null}
              {services.map((service) => (
                <SelectItem key={service.id} value={service.id}>
                  {service.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => handleInputChange("notes", e.target.value)}
          placeholder={t("requests.form.notes")}
          rows={3}
          className="resize-none"
        />
        <div className="flex justify-end gap-3 border-t pt-6">
          <Button type="button" variant="new_outline" onClick={onClose}>
            {t("requests.form.cancel")}
          </Button>
          <Button type="submit" variant="new_default">
            {t("requests.form.update")}
          </Button>
        </div>
      </form>
    </div>
  );
};
